/* modern-delivery-zones.js */
(function () {
  "use strict";

  // ── Brand configuration ──────────────────────────────────────────────────
  // Keyed by Shopify metaobject handle. If brand handles differ from these
  // defaults, update the keys to match. Colours + URLs are hardcoded pending
  // the colour field being added to the brand metaobject definition.

  var BRAND_CONFIG = {
    "big-springs-water": {
      label: "Regional & Central West NSW",
      color: "#365493",
      badgeBg: "#365493",
      badgeFg: "#ffffff",
      url: "https://bigspringswater.com.au/?utm_source=novoh2o.com.au&utm_medium=referral&utm_campaign=regional_partners",
    },
    "kiewa-valley-spring-water": {
      label: "Melbourne & Country Victoria",
      color: "#f645a9",
      badgeBg: "#f645a9",
      badgeFg: "#ffffff",
      url: "https://kiewavalleyspringwater.com.au/?utm_source=novoh2o.com.au&utm_medium=referral&utm_campaign=regional_partners",
    },
    "summer-springs": {
      label: "Australian Capital Territory",
      color: "#c2951c",
      badgeBg: "#f6e446",
      badgeFg: "#1b365c",
      url: "https://summersprings.com.au/?utm_source=novoh2o.com.au&utm_medium=referral&utm_campaign=regional_partners",
    },
  };

  // Fallback: derive handle from display name to cover handle mismatches
  function brandConfigByName(name) {
    if (!name) return null;
    var handle = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
    return BRAND_CONFIG[handle] || null;
  }

  // ── Storefront API ───────────────────────────────────────────────────────

  // Field keys mirror the `delivery_location` definition exactly: `suburb`,
  // `postcode` (singular, one value), `state`, `region` (a *list* of
  // `delivery_regions` references — entries really do carry more than one, so
  // every reference is pulled, not just the first), `page`. There is no
  // serviceability flag. Brand attribution is not on the location — it hangs
  // off the region's `service_by` reference, so it is resolved in the same
  // round trip.
  var GQL_QUERY = [
    "query GetDeliveryZones($cursor: String) {",
    '  metaobjects(type: "delivery_location", first: 250, after: $cursor) {',
    "    nodes {",
    '      suburb: field(key: "suburb") { value }',
    '      postcode: field(key: "postcode") { value }',
    '      state: field(key: "state") { value }',
    '      page: field(key: "page") {',
    "        reference { ... on Metaobject { handle } }",
    "      }",
    '      region: field(key: "region") {',
    "        references(first: 20) {",
    "          nodes {",
    "            ... on Metaobject {",
    "              handle",
    '              name: field(key: "name") { value }',
    '              service_by: field(key: "service_by") {',
    "                reference {",
    "                  ... on Metaobject {",
    "                    handle",
    '                    name: field(key: "name") { value }',
    "                  }",
    "                }",
    "              }",
    "            }",
    "          }",
    "        }",
    "      }",
    "    }",
    "    pageInfo { hasNextPage endCursor }",
    "  }",
    "}",
  ].join("\n");

  // `service_areas` entries are published as web pages under the definition's
  // "areas" URL prefix, so an entry handle of `penrith` resolves to
  // /pages/areas/penrith. Suburbs without a `page` reference stay plain text.
  var SERVICE_AREA_PATH = "/pages/areas/";

  function fetchPage(shopDomain, token, cursor) {
    return fetch("https://" + shopDomain + "/api/2025-01/graphql.json", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": token,
      },
      body: JSON.stringify({
        query: GQL_QUERY,
        variables: cursor ? { cursor: cursor } : {},
      }),
    })
      .then(function (resp) {
        if (!resp.ok) throw new Error("Storefront API responded " + resp.status);
        return resp.json();
      })
      .then(function (json) {
        if (json.errors && json.errors.length) throw new Error(json.errors[0].message);
        return json.data.metaobjects;
      });
  }

  function fetchAllZones(shopDomain, token) {
    var cacheKey = "mdzn_v4_" + shopDomain;
    try {
      var raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        var cached = JSON.parse(raw);
        if (cached && Date.now() - cached.ts < 3600000) return Promise.resolve(cached.zones);
      }
    } catch (_) {}

    var zones = [];

    function nextPage(cursor) {
      return fetchPage(shopDomain, token, cursor).then(function (page) {
        zones = zones.concat(page.nodes);
        if (page.pageInfo.hasNextPage) return nextPage(page.pageInfo.endCursor);
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({ zones: zones, ts: Date.now() }));
        } catch (_) {}
        return zones;
      });
    }

    return nextPage(null);
  }

  // ── Data normalization ───────────────────────────────────────────────────

  // Region label falls back to the metaobject handle so a region missing its
  // `name` field still groups sensibly instead of vanishing. `novo-h-20` is
  // the primary brand, so it is treated the same as no `service_by` at all.
  // Brand is resolved per region, not per zone: a suburb whose two regions
  // have different `service_by` values belongs to both territories.
  function normalizeRegion(node) {
    var brand = (node.service_by && node.service_by.reference) || null;
    var brandHandle = brand && brand.handle !== "novo-h-20" ? brand.handle : null;
    return {
      name: (node.name && node.name.value) || node.handle || "",
      brandHandle: brandHandle,
      brandName: brandHandle ? (brand.name && brand.name.value) || null : null,
    };
  }

  function normalizeZone(node) {
    var refs = (node.region && node.region.references && node.region.references.nodes) || [];
    var seen = {};

    return {
      suburb: (node.suburb && node.suburb.value) || "",
      postcodes: node.postcode && node.postcode.value ? [node.postcode.value] : [],
      state: (node.state && node.state.value) || "",
      regions: refs.map(normalizeRegion).filter(function (region) {
        if (!region.name || seen[region.name]) return false;
        seen[region.name] = true;
        return true;
      }),
      pageHandle: (node.page && node.page.reference && node.page.reference.handle) || null,
    };
  }

  // ── Territory building ───────────────────────────────────────────────────

  function buildTerritories(rawNodes, primaryLabel) {
    var zones = rawNodes.map(normalizeZone).filter(function (z) {
      return z.suburb;
    });

    // Group by brand handle (null = NovoH2O direct). A zone is expanded into
    // one entry per region it references, so a suburb in several regions is
    // listed under each of them — and under each brand those regions belong to.
    var brandGroups = {};
    var brandOrder = []; // preserve insertion order

    zones.forEach(function (zone) {
      var regions = zone.regions.length
        ? zone.regions
        : [{ name: "General", brandHandle: null, brandName: null }];

      regions.forEach(function (region) {
        var key = region.brandHandle || "__primary__";
        if (!brandGroups[key]) {
          brandGroups[key] = {
            brandHandle: region.brandHandle,
            brandName: region.brandName,
            entries: [],
          };
          brandOrder.push(key);
        }
        brandGroups[key].entries.push({ zone: zone, regionName: region.name });
      });
    });

    var territories = [];

    // Primary brand first
    if (brandGroups["__primary__"]) {
      territories.push(
        buildTerritory(brandGroups["__primary__"], primaryLabel || "Greater Sydney"),
      );
    }

    // Partner brands in the order they were first encountered
    brandOrder.forEach(function (key) {
      if (key === "__primary__") return;
      var group = brandGroups[key];
      var cfg = BRAND_CONFIG[group.brandHandle] || brandConfigByName(group.brandName) || {};
      territories.push(buildTerritory(group, cfg.label || group.brandName || group.brandHandle));
    });

    return territories;
  }

  function buildTerritory(group, label) {
    var cfg = group.brandHandle
      ? BRAND_CONFIG[group.brandHandle] || brandConfigByName(group.brandName) || {}
      : null;

    // Group the brand's (zone, region) entries by region name
    var regionMap = {};
    var regionOrder = [];

    group.entries.forEach(function (entry) {
      if (!regionMap[entry.regionName]) {
        regionMap[entry.regionName] = [];
        regionOrder.push(entry.regionName);
      }
      regionMap[entry.regionName].push(entry.zone);
    });

    regionOrder.sort(function (a, b) {
      return a.localeCompare(b);
    });

    var regions = regionOrder.map(function (name) {
      return {
        name: name,
        subs: regionMap[name].map(function (z) {
          return { n: z.suburb, state: z.state, postcodes: z.postcodes, page: z.pageHandle };
        }),
      };
    });

    return {
      id: group.brandHandle || "primary",
      label: label,
      partner: group.brandName || null,
      color: (cfg && cfg.color) || null,
      badgeBg: (cfg && cfg.badgeBg) || null,
      badgeFg: (cfg && cfg.badgeFg) || null,
      url: (cfg && cfg.url) || null,
      regions: regions,
    };
  }

  // ── Mapbox ───────────────────────────────────────────────────────────────
  // All seven pin layers already exist in the Mapbox style, pulling from a
  // single tileset (pixite.pnvha2b4p65x) split by a `layer_name` filter.
  // Nothing is added at runtime — we only flip layout visibility, so the
  // style stays the single source of truth for pin colour and placement.

  var PIN_LAYER_IDS = [
    "Big Springs Water",
    "Chill Freeze",
    "Kiewa Valley Water",
    "Melbourne",
    "Summer Springs",
    "Novo H20",
    "Southern Highlands",
  ];

  // Territory (brand handle, or "primary" for NovoH2O direct) -> its layers.
  // "Chill Freeze" and "Southern Highlands" deliberately share a colour with
  // "Big Springs Water" and "Novo H20" respectively so each pair reads as one
  // merged group on the map — so they travel together by default.
  var TERRITORY_LAYERS = {
    primary: ["Novo H20", "Southern Highlands"],
    "big-springs-water": ["Big Springs Water", "Chill Freeze"],
    "kiewa-valley-spring-water": ["Kiewa Valley Water", "Melbourne"],
    "summer-springs": ["Summer Springs"],
  };

  // Per-region narrowing, keyed "<territoryId>::<lowercased region name>".
  // Only listed where a region tab maps to a strict subset of its territory's
  // layers; anything absent falls back to TERRITORY_LAYERS above.
  //
  // The Kiewa territory splits across two layers ("Melbourne" and "Kiewa
  // Valley Water") but the zone data carries no field saying which regions
  // belong to which, so that territory shows both rather than guessing.
  // Add a line here once the split is confirmed, e.g.:
  //   "kiewa-valley-spring-water::melbourne metro": ["Melbourne"],
  var REGION_LAYERS = {
    "primary::southern highlands": ["Southern Highlands"],
  };

  // Opening camera, and the one "All Delivery Zones" flies back to. Set
  // explicitly rather than inherited from the style's saved camera so the
  // panel always opens on the same framing regardless of style edits.
  var DEFAULT_VIEW = { center: [150.9327, -33.7647], zoom: 4.5 };

  // Camera per territory. Applied only once a tab is explicitly selected.
  //
  // summer-springs is labelled "Australian Capital Territory" but its regions
  // are all central/northern NSW (Central West & Orana, New England, Tamworth
  // & North West Slopes) — so the camera frames that cluster, not Canberra.
  var TERRITORY_VIEW = {
    primary: { center: [151.0, -33.85], zoom: 8.4 },
    "big-springs-water": { center: [147.8, -33.2], zoom: 5.6 },
    "kiewa-valley-spring-water": { center: [145.6, -37.2], zoom: 6.2 },
    "summer-springs": { center: [150.6, -31.8], zoom: 5.9 },
  };

  // Camera per region, keyed like REGION_LAYERS ("<territoryId>::<lowercased
  // region name>"). Only worth an entry where a territory spans far enough
  // that its own framing leaves the region a smudge in the corner; anything
  // absent falls back to TERRITORY_VIEW.
  //
  // Same caveat as REGION_LAYERS: matched on the region *name* from the
  // metaobject, so a rename in the data silently drops back to the territory
  // camera rather than erroring.
  var REGION_VIEW = {
    "summer-springs::central west & orana": { center: [148.6, -32.25], zoom: 7.2 },
    "summer-springs::tamworth & north west slopes": { center: [150.93, -31.09], zoom: 7.3 },
    "summer-springs::new england & northern tablelands": { center: [151.67, -30.51], zoom: 7.3 },
  };

  // Section setting override for the tooltip's clickable layers. Returns null
  // when unset so the caller can fall back to PIN_LAYER_IDS.
  function parseLayerList(value) {
    if (!value) return null;
    var ids = String(value)
      .split(",")
      .map(function (id) {
        return id.trim();
      })
      .filter(Boolean);
    return ids.length ? ids : null;
  }

  function tabKey(territoryId, regionName) {
    return territoryId + "::" + String(regionName || "").toLowerCase();
  }

  function layersForTab(territoryId, regionName) {
    var key = tabKey(territoryId, regionName);
    return REGION_LAYERS[key] || TERRITORY_LAYERS[territoryId] || PIN_LAYER_IDS;
  }

  // Region camera first, then the territory's. Null means "leave the camera
  // where it is" — the aggregate view has no entry either way.
  function viewForTab(territoryId, regionName) {
    return REGION_VIEW[tabKey(territoryId, regionName)] || TERRITORY_VIEW[territoryId] || null;
  }

  // Visibility controller over the style's existing pin layers. Every method
  // takes a layer id or an array of them, and no-ops on ids the style doesn't
  // carry, so a renamed layer degrades to "not shown" instead of throwing.
  function createPinController(map) {
    function apply(ids, visible) {
      (Array.isArray(ids) ? ids : [ids]).forEach(function (id) {
        if (map.getLayer(id)) {
          map.setLayoutProperty(id, "visibility", visible ? "visible" : "none");
        }
      });
    }

    var controller = {
      setVisible: function (ids, visible) {
        apply(ids, visible !== false);
      },
      isVisible: function (id) {
        if (!map.getLayer(id)) return false;
        // Unset means visible — that is the Mapbox layout default.
        return map.getLayoutProperty(id, "visibility") !== "none";
      },
      toggle: function (id) {
        apply(id, !controller.isVisible(id));
      },
      showAll: function () {
        apply(PIN_LAYER_IDS, true);
      },
      hideAll: function () {
        apply(PIN_LAYER_IDS, false);
      },
    };

    return controller;
  }

  // mapbox-gl.js is a deferred CDN script emitted just above this file, so it
  // has normally already run. The listener covers theme-editor re-renders.
  function whenMapboxReady(cb) {
    if (window.mapboxgl) return cb();
    var script = document.querySelector("script[data-mdzn-mapbox]");
    if (!script) return;
    script.addEventListener("load", function () {
      cb();
    });
  }

  // ── Utilities ────────────────────────────────────────────────────────────

  function escHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function hexToRgba(hex, alpha) {
    var h = hex.replace("#", "");
    if (h.length === 3)
      h = h
        .split("")
        .map(function (c) {
          return c + c;
        })
        .join("");
    var n = parseInt(h, 16);
    return (
      "rgba(" + ((n >> 16) & 255) + "," + ((n >> 8) & 255) + "," + (n & 255) + "," + alpha + ")"
    );
  }

  // ── Demo data ────────────────────────────────────────────────────────────
  // Enabled via the "Demo mode" section setting. Mirrors the raw API node
  // shape so it passes through normalizeZone + buildTerritories unchanged.

  function makeZone(suburb, postcodes, state, region, brandHandle, brandName) {
    var names = Array.isArray(region) ? region : [region];
    return {
      suburb: { value: suburb },
      postcode: { value: postcodes[0] || "" },
      state: { value: state },
      region: {
        references: {
          nodes: names.map(function (name) {
            return {
              handle: name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
              name: { value: name },
              service_by: brandHandle
                ? { reference: { handle: brandHandle, name: { value: brandName } } }
                : null,
            };
          }),
        },
      },
    };
  }

  var DEMO_ZONES = [
    // Greater Sydney — NovoH2O direct
    makeZone("Parramatta", ["2150"], "NSW", "Western Sydney", null, null),
    makeZone("Blacktown", ["2148"], "NSW", "Western Sydney", null, null),
    makeZone("Penrith", ["2750"], "NSW", "Western Sydney", null, null),
    makeZone("Liverpool", ["2170"], "NSW", "Western Sydney", null, null),
    makeZone("Merrylands", ["2160"], "NSW", "Western Sydney", null, null),
    makeZone("Bondi", ["2026"], "NSW", "Eastern Suburbs", null, null),
    makeZone("Coogee", ["2034"], "NSW", "Eastern Suburbs", null, null),
    makeZone("Randwick", ["2031"], "NSW", "Eastern Suburbs", null, null),
    makeZone("Maroubra", ["2035"], "NSW", "Eastern Suburbs", null, null),
    makeZone("Double Bay", ["2028"], "NSW", "Eastern Suburbs", null, null),
    makeZone("Newtown", ["2042"], "NSW", "Inner West", null, null),
    makeZone("Marrickville", ["2204"], "NSW", "Inner West", null, null),
    makeZone("Leichhardt", ["2040"], "NSW", "Inner West", null, null),
    makeZone("Balmain", ["2041"], "NSW", "Inner West", null, null),
    makeZone("Manly", ["2095"], "NSW", "Northern Beaches", null, null),
    makeZone("Dee Why", ["2099"], "NSW", "Northern Beaches", null, null),
    makeZone("Brookvale", ["2100"], "NSW", "Northern Beaches", null, null),
    makeZone("Mona Vale", ["2103"], "NSW", "Northern Beaches", null, null),
    makeZone("North Sydney", ["2060"], "NSW", "Lower North Shore", null, null),
    makeZone("Chatswood", ["2067"], "NSW", "Lower North Shore", null, null),
    makeZone("Mosman", ["2088"], "NSW", "Lower North Shore", null, null),
    makeZone("Castle Hill", ["2154"], "NSW", "Hills District", null, null),
    makeZone("Baulkham Hills", ["2153"], "NSW", "Hills District", null, null),
    makeZone("Kellyville", ["2155"], "NSW", "Hills District", null, null),
    makeZone("Hurstville", ["2220"], "NSW", "South Sydney", null, null),
    makeZone("Kogarah", ["2217"], "NSW", "South Sydney", null, null),
    makeZone("Cronulla", ["2230"], "NSW", "Sutherland Shire", null, null),
    makeZone("Miranda", ["2228"], "NSW", "Sutherland Shire", null, null),
    makeZone("Sydney CBD", ["2000"], "NSW", "Sydney CBD", null, null),
    makeZone("Surry Hills", ["2010"], "NSW", "Sydney CBD", null, null),
    makeZone("Bowral", ["2576"], "NSW", "Southern Highlands", null, null),
    makeZone("Mittagong", ["2575"], "NSW", "Southern Highlands", null, null),
    // Big Springs Water — Regional NSW
    makeZone("Bathurst", ["2795"], "NSW", "Central West", "big-springs-water", "Big Springs Water"),
    makeZone("Orange", ["2800"], "NSW", "Central West", "big-springs-water", "Big Springs Water"),
    makeZone("Dubbo", ["2830"], "NSW", "Central West", "big-springs-water", "Big Springs Water"),
    makeZone("Wagga Wagga", ["2650"], "NSW", "Riverina", "big-springs-water", "Big Springs Water"),
    makeZone("Albury", ["2640"], "NSW", "Riverina", "big-springs-water", "Big Springs Water"),
    makeZone("Tamworth", ["2340"], "NSW", "New England", "big-springs-water", "Big Springs Water"),
    // Kiewa Valley Spring Water — Victoria
    makeZone(
      "Melbourne CBD",
      ["3000"],
      "VIC",
      "Melbourne Metro",
      "kiewa-valley-spring-water",
      "Kiewa Valley Spring Water",
    ),
    makeZone(
      "Richmond",
      ["3121"],
      "VIC",
      "Melbourne Metro",
      "kiewa-valley-spring-water",
      "Kiewa Valley Spring Water",
    ),
    makeZone(
      "St Kilda",
      ["3182"],
      "VIC",
      "Melbourne Metro",
      "kiewa-valley-spring-water",
      "Kiewa Valley Spring Water",
    ),
    makeZone(
      "Shepparton",
      ["3630"],
      "VIC",
      "Goulburn Valley",
      "kiewa-valley-spring-water",
      "Kiewa Valley Spring Water",
    ),
    makeZone(
      "Wangaratta",
      ["3677"],
      "VIC",
      "Goulburn Valley",
      "kiewa-valley-spring-water",
      "Kiewa Valley Spring Water",
    ),
    makeZone(
      "Bendigo",
      ["3550"],
      "VIC",
      "Bendigo & Ballarat",
      "kiewa-valley-spring-water",
      "Kiewa Valley Spring Water",
    ),
    makeZone(
      "Ballarat",
      ["3350"],
      "VIC",
      "Bendigo & Ballarat",
      "kiewa-valley-spring-water",
      "Kiewa Valley Spring Water",
    ),
    // Summer Springs — ACT
    makeZone(
      "Canberra City",
      ["2601"],
      "ACT",
      "Canberra Central",
      "summer-springs",
      "Summer Springs",
    ),
    makeZone("Braddon", ["2612"], "ACT", "Canberra Central", "summer-springs", "Summer Springs"),
    makeZone("Kingston", ["2604"], "ACT", "Canberra Central", "summer-springs", "Summer Springs"),
    makeZone("Belconnen", ["2617"], "ACT", "Belconnen", "summer-springs", "Summer Springs"),
    makeZone("Gungahlin", ["2912"], "ACT", "Gungahlin", "summer-springs", "Summer Springs"),
    makeZone("Tuggeranong", ["2900"], "ACT", "Tuggeranong", "summer-springs", "Summer Springs"),
  ];

  // ── Component init ───────────────────────────────────────────────────────

  function initDeliveryZones(cfg) {
    var sid = cfg.sid;
    var token = cfg.token;
    var phone = cfg.phone;
    var primaryLabel = cfg.primaryLabel;
    var demoMode = cfg.demoMode;
    var mapboxToken = cfg.mapboxToken;
    var mapboxStyle = cfg.mapboxStyle;
    var mapTooltips = cfg.mapTooltips;
    // Blank falls back to every pin layer. queryRenderedFeatures only returns
    // rendered features, so the tooltip follows layer visibility for free.
    var tooltipLayers = parseLayerList(cfg.tooltipLayers) || PIN_LAYER_IDS;

    var root = document.getElementById(sid);
    if (!root) return;

    var loadingEl = document.getElementById(sid + "-loading");
    var errorEl = document.getElementById(sid + "-error");
    var panelEl = document.getElementById(sid + "-panel");
    var searchEl = root.querySelector(".mdzn__search-input");
    var territoryEl = document.getElementById(sid + "-territory");
    var railEl = document.getElementById(sid + "-rail");
    var gridEl = document.getElementById(sid + "-grid");
    var mapWrapEl = document.getElementById(sid + "-map-wrap");
    var mapEl = document.getElementById(sid + "-map");
    var servicedEl = document.getElementById(sid + "-serviced-by");
    var partnersEl = document.getElementById(sid + "-partners");
    var retryBtn = root.querySelector(".mdzn__retry-btn");

    var shopDomain = (window.Shopify && window.Shopify.shop) || location.hostname;

    var territories = [];
    var state = { territory: 0, region: 0, search: "" };

    // Map lifecycle. `tabSelected` stays false until the visitor actually
    // picks a territory or region — until then every pin layer stays visible
    // and the camera sits wherever the Mapbox style is saved.
    var map = null;
    var pins = null;
    var popup = null;
    var pendingTab = null;
    var tabSelected = false;
    var mapResizeObserver = null;

    // ── Render helpers ──────────────────────────────────────────────────

    function currentTerritory() {
      return territories[state.territory] || territories[0];
    }

    // -1 means "no region narrowed". The aggregate territory opens that way so
    // the grid lists every region and the map keeps all pin layers on; brand
    // territories keep opening on their first region as they always have.
    function accentColor() {
      return currentTerritory().color || "var(--sky-400)";
    }
    function accentTint() {
      var col = currentTerritory().color;
      return col ? hexToRgba(col, 0.12) : "var(--sky-50)";
    }
    function accentText() {
      return currentTerritory().color || "var(--navy-700)";
    }

    function renderTerritorySelect() {
      var html = "";
      territories.forEach(function (t, i) {
        html +=
          '<option value="' +
          i +
          '"' +
          (i === state.territory ? " selected" : "") +
          ">" +
          escHtml(t.label) +
          "</option>";
      });
      territoryEl.innerHTML = html;
    }

    function renderServicedBy() {
      var t = currentTerritory();
      if (t.partner) {
        servicedEl.innerHTML =
          'Serviced by <strong style="color:' +
          escHtml(t.color || "inherit") +
          ';font-weight:700">' +
          escHtml(t.partner) +
          "</strong>";
        servicedEl.hidden = false;
      } else {
        servicedEl.hidden = true;
      }
    }

    function renderRegionRail() {
      var t = currentTerritory();
      var searching = state.search.trim().length > 0;
      var html = "";
      t.regions.forEach(function (r, i) {
        var active = !searching && i === state.region;
        var bg = active ? accentTint() : "transparent";
        var color = active ? accentText() : "";
        var badgeBg = active ? accentColor() : "var(--grey-100)";
        var badgeFg = active ? "#ffffff" : "var(--grey-500)";
        html +=
          '<button class="mdzn__region-btn' +
          (active ? " is-active" : "") +
          '" ' +
          'role="tab" aria-selected="' +
          active +
          '" ' +
          'style="background:' +
          bg +
          ";color:" +
          color +
          '" ' +
          'data-ridx="' +
          i +
          '">' +
          "<span>" +
          escHtml(r.name) +
          "</span>" +
          '<span class="mdzn__region-count" style="background:' +
          badgeBg +
          ";color:" +
          badgeFg +
          '">' +
          r.subs.length +
          "</span>" +
          "</button>";
      });
      railEl.innerHTML = html;
    }

    function groupsForSearch() {
      var t = currentTerritory();
      var q = state.search.trim().toLowerCase();
      if (!q) return [t.regions[state.region] || t.regions[0]];
      var out = [];
      t.regions.forEach(function (r) {
        var subs = r.subs.filter(function (s) {
          return (
            s.n.toLowerCase().includes(q) ||
            s.postcodes.some(function (p) {
              return p.startsWith(q);
            })
          );
        });
        if (subs.length) out.push({ name: r.name, subs: subs });
      });
      return out;
    }

    function renderGrid() {
      var q = state.search.trim();
      var groups = groupsForSearch();
      var total = groups.reduce(function (n, g) {
        return n + g.subs.length;
      }, 0);

      if (!total && q) {
        gridEl.innerHTML =
          '<div class="mdzn__no-results">No suburbs match your search.' +
          (phone
            ? " Call <strong>" + escHtml(phone) + "</strong> and we'll confirm your area."
            : "") +
          "</div>";
        return;
      }

      var html = "";
      groups.forEach(function (g) {
        html +=
          '<div class="mdzn__group">' +
          '<div class="mdzn__group-header">' +
          '<span class="mdzn__group-name">' +
          escHtml(g.name) +
          "</span>" +
          '<span class="mdzn__group-rule" aria-hidden="true"></span>' +
          '<span class="mdzn__group-count">' +
          g.subs.length +
          "</span>" +
          "</div>" +
          '<div class="mdzn__suburbs">';
        g.subs.forEach(function (s) {
          html += s.page
            ? '<a class="mdzn__suburb mdzn__suburb--link" href="' +
              escHtml(SERVICE_AREA_PATH + s.page) +
              '">' +
              escHtml(s.n) +
              "</a>"
            : '<div class="mdzn__suburb">' + escHtml(s.n) + "</div>";
        });
        html += "</div></div>";
      });
      gridEl.innerHTML = html;
    }

    function initMap() {
      if (map || !mapboxToken || !mapEl) return;

      whenMapboxReady(function () {
        if (!window.mapboxgl || map) return;

        window.mapboxgl.accessToken = mapboxToken;

        map = new window.mapboxgl.Map({
          container: mapEl,
          style: mapboxStyle,
          center: DEFAULT_VIEW.center,
          zoom: DEFAULT_VIEW.zoom,
          cooperativeGestures: true,
        });

        map.addControl(new window.mapboxgl.NavigationControl({ showCompass: false }), "top-right");

        map.on("load", function () {
          pins = createPinController(map);
          if (mapTooltips && window.NovoMapPopup) {
            popup = window.NovoMapPopup.attach(map, { layers: tooltipLayers });
          }
          if (pendingTab) {
            applyMapTab(pendingTab);
            pendingTab = null;
          }
        });

        // The wrap collapses to zero height during search and the layout
        // reflows at the mobile breakpoint; both need a gl canvas resize.
        if (window.ResizeObserver) {
          mapResizeObserver = new ResizeObserver(function () {
            if (map) map.resize();
          });
          mapResizeObserver.observe(mapEl);
        }
      });
    }

    function applyMapTab(tab) {
      if (!pins) {
        pendingTab = tab;
        return;
      }
      // The open tooltip may point at a pin this tab hides.
      if (popup) popup.close();

      pins.hideAll();
      pins.setVisible(layersForTab(tab.territoryId, tab.regionName), true);

      var view = viewForTab(tab.territoryId, tab.regionName);
      // flyTo already degrades to a jump under prefers-reduced-motion.
      if (view) map.flyTo({ center: view.center, zoom: view.zoom, duration: 900 });
    }

    function renderMap() {
      var searching = state.search.trim().length > 0;
      mapWrapEl.classList.toggle("is-collapsed", searching);
      if (searching || !tabSelected) return;

      var t = currentTerritory();
      // With no region narrowed, fall back to the territory itself — which for
      // the aggregate view means all pin layers and the style's own camera.
      var region = t.regions[state.region];
      applyMapTab({ territoryId: t.id, regionName: region && region.name });
    }

    function renderPartners() {
      var withUrl = territories.filter(function (t) {
        return t.partner && t.url;
      });
      var footerEl = partnersEl.closest(".mdzn__footer");
      if (!withUrl.length) {
        if (footerEl) footerEl.hidden = true;
        return;
      }
      var html = "";
      withUrl.forEach(function (t) {
        html +=
          '<a href="' +
          escHtml(t.url) +
          '" target="_blank" rel="noopener" class="mdzn__partner-badge" ' +
          'style="background:' +
          escHtml(t.badgeBg || t.color) +
          ";color:" +
          escHtml(t.badgeFg || "#fff") +
          '">' +
          escHtml(t.partner) +
          '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<path d="M7 17 17 7"/><path d="M8 7h9v9"/></svg>' +
          "</a>";
      });
      partnersEl.innerHTML = html;
    }

    function renderAll() {
      renderTerritorySelect();
      renderServicedBy();
      renderRegionRail();
      renderGrid();
      renderMap();
    }

    // ── Event handlers ──────────────────────────────────────────────────

    territoryEl.addEventListener("change", function () {
      var idx = parseInt(this.value, 10);
      state = { territory: idx, region: 0, search: "" };
      // Changing territory re-renders the rail with its first region active,
      // which is as much an explicit tab selection as clicking one.
      tabSelected = true;
      searchEl.value = "";
      renderAll();
    });

    railEl.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-ridx]");
      if (!btn) return;
      state.region = parseInt(btn.dataset.ridx, 10);
      state.search = "";
      tabSelected = true;
      searchEl.value = "";
      renderAll();
    });

    searchEl.addEventListener("input", function () {
      state.search = this.value;
      renderRegionRail();
      renderGrid();
      renderMap();
    });

    if (retryBtn) retryBtn.addEventListener("click", load);

    // ── Visibility helpers ───────────────────────────────────────────────

    function showLoading() {
      loadingEl.hidden = false;
      errorEl.hidden = true;
      panelEl.hidden = true;
    }

    function showError(msg) {
      var p = errorEl.querySelector("p");
      if (msg && p) p.textContent = msg;
      loadingEl.hidden = true;
      errorEl.hidden = false;
      panelEl.hidden = true;
    }

    function showPanel() {
      loadingEl.hidden = true;
      errorEl.hidden = true;
      panelEl.hidden = false;
      // Deferred until the panel is visible — a gl canvas built inside a
      // `hidden` container initialises at zero size.
      initMap();
    }

    // Theme-editor re-render: drop the WebGL context and the observer, which
    // otherwise outlive the section's DOM.
    function onSectionUnload(e) {
      if (e.detail && e.detail.sectionId !== sid.replace(/^mdzn-/, "")) return;
      if (mapResizeObserver) mapResizeObserver.disconnect();
      if (popup) popup.detach();
      if (map) map.remove();
      map = null;
      pins = null;
      popup = null;
      mapResizeObserver = null;
      document.removeEventListener("shopify:section:unload", onSectionUnload);
    }

    document.addEventListener("shopify:section:unload", onSectionUnload);

    // ── Load ─────────────────────────────────────────────────────────────

    function load() {
      if (demoMode) {
        territories = buildTerritories(DEMO_ZONES, primaryLabel);
        renderPartners();
        renderAll();
        showPanel();
        return;
      }
      if (!token) {
        showError("Storefront API token is not configured — edit this section to add one.");
        return;
      }
      showLoading();
      fetchAllZones(shopDomain, token)
        .then(function (rawNodes) {
          territories = buildTerritories(rawNodes, primaryLabel);
          if (!territories.length) throw new Error("No serviceable zones found");
          renderPartners();
          renderAll();
          showPanel();
        })
        .catch(function (err) {
          console.error("[mdzn] Failed to load delivery zones:", err);
          showError(null);
        });
    }

    load();
  }

  // ── Queue processing ─────────────────────────────────────────────────────

  function processAll() {
    (Array.isArray(window.mdznConfigs) ? window.mdznConfigs : []).forEach(initDeliveryZones);
    window.mdznConfigs = { push: initDeliveryZones };
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", processAll);
  } else {
    processAll();
  }
})();
